/*Package importing*/
import React, { useState, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom'

/*Import custom components and assets*/
import H1 from '../../components/heading/h1';
import service from '../../services/service';
import {
  isPasskeySupported,
  isConditionalUIAvailable,
  getWebauthnState,
  signInWithPasskey,
} from '../../services/webauthn';
import { identifyDatadogUser } from '../../datadog';

const LoginWidget = (props) => {
  const [loading, setLoading] = useState(false);
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [errorMessage, setErrorMessage] = useState('');

  // Passkey state
  const [passkeyEnabled, setPasskeyEnabled] = useState(false);
  const [passkeyLoading, setPasskeyLoading] = useState(false);
  const conditionalRef = useRef(false);

  // Fire conditional UI in the background as soon as we know login is on.
  // The browser will surface enrolled passkeys in the email field's autofill;
  // clicking one runs the ceremony — no email typing required. Defined as a
  // standalone function so we can re-arm it after a non-conditional sign-in
  // attempt aborts the existing one.
  const persistAndRedirect = (response) => {
    sessionStorage.setItem('zappUser', true);
    sessionStorage.setItem('accessToken', response.access_token);
    sessionStorage.setItem('data', JSON.stringify(response.data));
    sessionStorage.setItem('accessLevel', response.data?.access_level);
    if (response.data?.admin_permissions) {
      sessionStorage.setItem('permissions', JSON.stringify(response.data.admin_permissions));
    }
    identifyDatadogUser();
    const redirectPath = localStorage.getItem('redirectAfterLogin');
    localStorage.removeItem('redirectAfterLogin');
    window.location.href = redirectPath || '/dashboard';
  };

  useEffect(() => {
    let cancelled = false;
    (async () => {
      if (!isPasskeySupported()) return;
      const state = await getWebauthnState();
      if (cancelled) return;
      const enabled = !!state?.login_enabled;
      setPasskeyEnabled(enabled);
      if (!enabled) return;

      const conditionalOk = await isConditionalUIAvailable();
      if (cancelled || !conditionalOk || conditionalRef.current) return;

      conditionalRef.current = true;
      // Awaits until the user clicks a passkey suggestion OR the page navigates
      // away OR the user submits a different form (browsers auto-abort it).
      const response = await signInWithPasskey({ conditional: true });
      if (cancelled) return;
      if (response?.status) {
        persistAndRedirect(response);
      }
      // Silent on cancel / no passkey — conditional UI is meant to be a hint,
      // not a blocking dialog. Errors stay invisible unless explicit click.
    })();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const sendOTPClick = async () => {
    setLoading(true);
    if (!email || !password) {
      setErrorMessage("All fields are mandatory.");
      setLoading(false);
      return false;
    }
    // call api
    var params = {};
    var token = "Basic " + btoa(email + ":" + password);
    var response = await service(true, "admin/auth/login", params, token, false);
    console.log(response);
    if (response.status) {
      setLoading(false);
      props.sendOTPSuccess(response.data);
    } else {
      console.log(response);
      setLoading(false);
      setErrorMessage(response.message);
    }
  };

  const passkeySignInClick = async () => {
    if (passkeyLoading) return;
    setPasskeyLoading(true);
    setErrorMessage('');
    // Explicit click flow — no email, modal prompt picks the credential.
    const response = await signInWithPasskey({ conditional: false });
    if (response?.status) {
      persistAndRedirect(response);
    } else if (!response?.cancelled) {
      setErrorMessage(response?.message || 'Passkey sign-in failed.');
      setPasskeyLoading(false);
    } else {
      setPasskeyLoading(false);
    }
  };

  return (
    <div className="e-auth-widget-wrap">
      <H1 headOne="Tranna" headTwo="Admin Login." />
      <dd className="e-input-contentbox mb-4">
        <input
          id="uName"
          type="text"
          autoComplete="username webauthn"
          value={email}
          onChange={(event) => {
            setEmail(event.target.value);
            setErrorMessage("");
          }}
          required />
        <label htmlFor="uName">Username</label>
      </dd>
      <dd className="e-input-contentbox mb-2 mt-3">
        <input
          id="uPass"
          type="password"
          autoComplete="current-password"
          value={password}
          onKeyDown={(e) => e.keyCode === 13 && sendOTPClick()}
          onChange={(event) => {
            setPassword(event.target.value);
            setErrorMessage("");
          }}
          required />
        <label htmlFor="uPass">Password</label>
      </dd>
      <div className="col-lg-12 e-error-red">
        {
          errorMessage
        }
      </div>
      <div className='col-12 text-end'>
        <Link className='me-auto' to="/forgot-password">Forgot password</Link>
      </div>

      <div className={`col-12 p-0 mt-4 ${loading === true ? "e-auth-loader" : ""}`}>
        <span className="e-p-btn e-auth-btn" onClick={sendOTPClick}>
          <div>Send OTP</div>
        </span>
      </div>

      {passkeyEnabled && (
        <div className="col-12 p-0 mt-3">
          <div style={{ textAlign: 'center', color: '#888', fontSize: '12px', marginBottom: '8px' }}>
            — or —
          </div>
          <span
            className={`e-p-btn e-auth-btn ${passkeyLoading ? 'e-auth-loader' : ''}`}
            onClick={passkeySignInClick}
            style={{
              background: '#fff',
              color: '#0E4C55',
              border: '2px solid #0E4C55',
              cursor: passkeyLoading ? 'wait' : 'pointer',
            }}
          >
            <div>{passkeyLoading ? 'Waiting for device…' : 'Sign in with passkey'}</div>
          </span>
          <div style={{ textAlign: 'center', color: '#888', fontSize: '11px', marginTop: '6px' }}>
            No email needed — your browser will pick the right passkey.
          </div>
        </div>
      )}
    </div>
  );
};

export default LoginWidget;
